import React, { useState, useEffect } from 'react';
import {
  Button,
  Header,
  Segment,
  Message,
  Form,
  Confirm,
  Icon
} from 'semantic-ui-react';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { formatDate } from '../../../utils/dateUtils';

interface CloseBookQuestion {
  id: string;
  text: string;
  points: number;
}

interface Test {
  id: string;
  testName: string;
  type: 'closeBook' | 'verseMemorization';
  totalGrade: number;
  criteria?: {
    shortAnswer: { description: string; grade: number };
    longAnswer: { description: string; grade: number };
  };
  questions?: {
    shortAnswer: CloseBookQuestion[];
    longAnswer: CloseBookQuestion[];
  };
  config?: {
    shortAnswer: { totalQuestions: number; requiredAnswers: number; pointsPerQuestion: number };
    longAnswer: { totalQuestions: number; requiredAnswers: number; pointsPerQuestion: number };
  };
}

interface GradeCloseBookTestProps {
  test: Test;
  moduleId: string;
  submissionId: string;
  onBack: () => void;
}

interface QuestionGrade {
  questionId: string;
  grade: number;
  maxGrade: number;
  comment: string;
}

const GradeCloseBookTest: React.FC<GradeCloseBookTestProps> = ({
  test,
  moduleId,
  submissionId,
  onBack
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submission, setSubmission] = useState<any>(null);
  const [shortAnswerGrades, setShortAnswerGrades] = useState<QuestionGrade[]>([]);
  const [longAnswerGrades, setLongAnswerGrades] = useState<QuestionGrade[]>([]);
  const [verseGrade, setVerseGrade] = useState<number>(0);
  const [comments, setComments] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [isGraded, setIsGraded] = useState(false);

  // Legacy format
  const [legacyShortGrade, setLegacyShortGrade] = useState<number>(0);
  const [legacyLongGrade, setLegacyLongGrade] = useState<number>(0);

  useEffect(() => {
    const fetchSubmission = async () => {
      try {
        const submissionDoc = await getDoc(
          doc(db, 'modules', moduleId, 'tests', test.id, 'submissions', submissionId)
        );

        if (submissionDoc.exists()) {
          const data = submissionDoc.data();
          setSubmission(data);

          if (data.gradingStatus === 'graded') {
            setComments(data.graderComments || '');
            setIsGraded(true);

            if (test.type === 'closeBook' && test.questions) {
              if (data.grades?.shortAnswer) {
                setShortAnswerGrades(data.grades.shortAnswer);
              }
              if (data.grades?.longAnswer) {
                setLongAnswerGrades(data.grades.longAnswer);
              }
            } else if (test.type === 'closeBook' && test.criteria) {
              setLegacyShortGrade(data.grades?.shortAnswer ?? 0);
              setLegacyLongGrade(data.grades?.longAnswer ?? 0);
            } else {
              setVerseGrade(data.grade ?? 0);
            }
          } else {
            if (test.type === 'closeBook' && test.questions) {
              const sa: QuestionGrade[] = (data.answers?.shortAnswer || []).map(
                (a: { questionId: string; answer: string }) => {
                  const q = test.questions!.shortAnswer.find(x => x.id === a.questionId);
                  return {
                    questionId: a.questionId,
                    grade: 0,
                    maxGrade: q?.points ?? 0,
                    comment: ''
                  };
                }
              );
              const la: QuestionGrade[] = (data.answers?.longAnswer || []).map(
                (a: { questionId: string; answer: string }) => {
                  const q = test.questions!.longAnswer.find(x => x.id === a.questionId);
                  return {
                    questionId: a.questionId,
                    grade: 0,
                    maxGrade: q?.points ?? 0,
                    comment: ''
                  };
                }
              );
              setShortAnswerGrades(sa);
              setLongAnswerGrades(la);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching submission:', err);
        setError('Failed to load submission');
      } finally {
        setLoading(false);
      }
    };

    fetchSubmission();
  }, [moduleId, test.id, submissionId, test.type]);

  const handleQuestionGradeChange = (
    type: 'shortAnswer' | 'longAnswer',
    questionId: string,
    grade: number,
    maxGrade: number
  ) => {
    const updater = type === 'shortAnswer' ? setShortAnswerGrades : setLongAnswerGrades;
    updater(prev =>
      prev.map(g =>
        g.questionId === questionId ? { ...g, grade, maxGrade } : g
      )
    );
  };

  const handleQuestionCommentChange = (
    type: 'shortAnswer' | 'longAnswer',
    questionId: string,
    comment: string
  ) => {
    const updater = type === 'shortAnswer' ? setShortAnswerGrades : setLongAnswerGrades;
    updater(prev =>
      prev.map(g => (g.questionId === questionId ? { ...g, comment } : g))
    );
  };

  const computedTotal =
    test.type === 'closeBook' && test.questions
      ? (() => {
          const saRequired = test.config?.shortAnswer?.requiredAnswers ?? 10;
          const laRequired = test.config?.longAnswer?.requiredAnswers ?? 5;
          const bestSa = [...shortAnswerGrades]
            .sort((a, b) => b.grade - a.grade)
            .slice(0, saRequired)
            .reduce((s, g) => s + g.grade, 0);
          const bestLa = [...longAnswerGrades]
            .sort((a, b) => b.grade - a.grade)
            .slice(0, laRequired)
            .reduce((s, g) => s + g.grade, 0);
          return bestSa + bestLa;
        })()
      : test.type === 'closeBook' && test.criteria
      ? legacyShortGrade + legacyLongGrade
      : verseGrade;

  const isSubmitDisabled = () => {
    if (isGraded) return true;
    if (!comments.trim()) return true;

    if (test.type === 'closeBook' && test.questions) {
      const saValid = shortAnswerGrades.every(
        g => g.grade >= 0 && g.grade <= g.maxGrade
      );
      const laValid = longAnswerGrades.every(
        g => g.grade >= 0 && g.grade <= g.maxGrade
      );
      return !saValid || !laValid;
    }
    if (test.type === 'closeBook' && test.criteria) {
      return (
        legacyShortGrade < 0 ||
        legacyShortGrade > test.criteria.shortAnswer.grade ||
        legacyLongGrade < 0 ||
        legacyLongGrade > test.criteria.longAnswer.grade
      );
    }
    return verseGrade < 0 || verseGrade > test.totalGrade;
  };

  const handleSubmit = async () => {
    try {
      const submissionRef = doc(
        db,
        'modules',
        moduleId,
        'tests',
        test.id,
        'submissions',
        submissionId
      );

      const updateData: Record<string, unknown> = {
        gradingStatus: 'graded',
        gradedAt: Timestamp.now(),
        graderComments: comments
      };

      if (test.type === 'closeBook' && test.questions) {
        updateData.grades = {
          shortAnswer: shortAnswerGrades,
          longAnswer: longAnswerGrades
        };
        updateData.grade = computedTotal;
      } else if (test.type === 'closeBook' && test.criteria) {
        updateData.grades = {
          shortAnswer: legacyShortGrade,
          longAnswer: legacyLongGrade
        };
        updateData.grade = legacyShortGrade + legacyLongGrade;
      } else {
        updateData.grade = verseGrade;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await updateDoc(submissionRef, updateData as any);
      setIsGraded(true);
      setShowConfirm(false);
      setTimeout(() => onBack(), 1000);
    } catch (err) {
      console.error('Error submitting grades:', err);
      setError('Failed to submit grades');
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!submission) return <Message negative>Submission not found</Message>;

  const submittedAt = submission.submittedAt?.toDate
    ? submission.submittedAt.toDate()
    : submission.submittedAt;

  const getAnswer = (questionId: string, type: 'short' | 'long') => {
    const arr =
      type === 'short'
        ? submission.answers?.shortAnswer
        : submission.answers?.longAnswer;
    return arr?.find((a: { questionId: string }) => a.questionId === questionId)
      ?.answer ?? '';
  };

  return (
    <div>
      <Button
        color="grey"
        icon
        labelPosition="left"
        onClick={onBack}
        style={{ marginBottom: '1rem' }}
      >
        <Icon name="arrow left" />
        Back to Submissions
      </Button>

      <Header as="h2">Grade Submission #{submissionId.slice(-6)}</Header>

      {error && <Message negative>{error}</Message>}

      <Segment>
        <Header as="h3">Submission Details</Header>
        <p><strong>Submitted on:</strong> {formatDate(submittedAt)}</p>
        {submission.pdfUrl && (
          <Button
            as="a"
            href={submission.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            primary
          >
            View Submitted PDF
          </Button>
        )}
      </Segment>

      {test.type === 'closeBook' && test.questions && submission.answers && (
        <>
          <Message info>
            Grade all answered questions. The total will use the best{' '}
            {test.config?.shortAnswer?.requiredAnswers ?? 10} short answer grades
            and best {test.config?.longAnswer?.requiredAnswers ?? 5} long answer
            grades.
          </Message>
          <Header as="h3">Short Answer Questions</Header>
          {test.questions.shortAnswer.map((q, idx) => {
            const answer = getAnswer(q.id, 'short');
            const gradeEntry = shortAnswerGrades.find(g => g.questionId === q.id);
            if (!answer && !gradeEntry) return null;

            return (
              <Segment key={q.id}>
                <p><strong>Question {idx + 1} ({q.points} pts):</strong> {q.text}</p>
                <p><strong>Student Answer:</strong></p>
                <p style={{ whiteSpace: 'pre-wrap', marginLeft: '1rem' }}>
                  {answer || '(No answer)'}
                </p>
                <Form.Field>
                  <label>Grade (0 - {q.points})</label>
                  <Form.Input
                    type="number"
                    value={gradeEntry?.grade ?? 0}
                    onChange={(e) =>
                      handleQuestionGradeChange(
                        'shortAnswer',
                        q.id,
                        Number(e.target.value),
                        q.points
                      )
                    }
                    min={0}
                    max={q.points}
                    disabled={isGraded}
                  />
                </Form.Field>
                <Form.Field>
                  <label>Comment (optional)</label>
                  <Form.Input
                    value={gradeEntry?.comment ?? ''}
                    onChange={(e) =>
                      handleQuestionCommentChange(
                        'shortAnswer',
                        q.id,
                        e.target.value
                      )
                    }
                    disabled={isGraded}
                  />
                </Form.Field>
              </Segment>
            );
          })}

          <Header as="h3">Long Answer Questions</Header>
          {test.questions.longAnswer.map((q, idx) => {
            const answer = getAnswer(q.id, 'long');
            const gradeEntry = longAnswerGrades.find(g => g.questionId === q.id);
            if (!answer && !gradeEntry) return null;

            return (
              <Segment key={q.id}>
                <p><strong>Question {idx + 1} ({q.points} pts):</strong> {q.text}</p>
                <p><strong>Student Answer:</strong></p>
                <p style={{ whiteSpace: 'pre-wrap', marginLeft: '1rem' }}>
                  {answer || '(No answer)'}
                </p>
                <Form.Field>
                  <label>Grade (0 - {q.points})</label>
                  <Form.Input
                    type="number"
                    value={gradeEntry?.grade ?? 0}
                    onChange={(e) =>
                      handleQuestionGradeChange(
                        'longAnswer',
                        q.id,
                        Number(e.target.value),
                        q.points
                      )
                    }
                    min={0}
                    max={q.points}
                    disabled={isGraded}
                  />
                </Form.Field>
                <Form.Field>
                  <label>Comment (optional)</label>
                  <Form.Input
                    value={gradeEntry?.comment ?? ''}
                    onChange={(e) =>
                      handleQuestionCommentChange(
                        'longAnswer',
                        q.id,
                        e.target.value
                      )
                    }
                    disabled={isGraded}
                  />
                </Form.Field>
              </Segment>
            );
          })}
        </>
      )}

      {test.type === 'verseMemorization' && submission.answer !== undefined && (
        <Segment>
          <Header as="h3">Student Answer</Header>
          <p style={{ whiteSpace: 'pre-wrap' }}>{submission.answer}</p>
          <Form.Field>
            <label>Grade (0 - {test.totalGrade})</label>
            <Form.Input
              type="number"
              value={verseGrade}
              onChange={(e) => setVerseGrade(Number(e.target.value))}
              min={0}
              max={test.totalGrade}
              disabled={isGraded}
            />
          </Form.Field>
        </Segment>
      )}

      {test.type === 'closeBook' && test.criteria && submission.pdfUrl && (
        <Segment>
          <Header as="h3">Grading (Legacy Format)</Header>
          <Form.Field>
            <label>Short Answer (0 - {test.criteria.shortAnswer.grade})</label>
            <Form.Input
              type="number"
              value={legacyShortGrade}
              onChange={(e) => setLegacyShortGrade(Number(e.target.value))}
              min={0}
              max={test.criteria.shortAnswer.grade}
              disabled={isGraded}
            />
          </Form.Field>
          <Form.Field>
            <label>Long Answer (0 - {test.criteria.longAnswer.grade})</label>
            <Form.Input
              type="number"
              value={legacyLongGrade}
              onChange={(e) => setLegacyLongGrade(Number(e.target.value))}
              min={0}
              max={test.criteria.longAnswer.grade}
              disabled={isGraded}
            />
          </Form.Field>
        </Segment>
      )}

      <Form>
        <Segment>
          <Header as="h3">Overall Comments</Header>
          <Form.TextArea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            disabled={isGraded}
          />
        </Segment>

        <p><strong>Total Grade: {computedTotal}</strong> / {test.totalGrade}</p>

        <Button
          color="green"
          onClick={() => setShowConfirm(true)}
          disabled={isSubmitDisabled()}
        >
          Submit Grades
        </Button>
      </Form>

      <Confirm
        open={showConfirm}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleSubmit}
      />
    </div>
  );
};

export default GradeCloseBookTest;
